/**
 * Validation Schemas and Guards
 * 
 * Input validation and type guards for the expense splitter.
 * Following dev guide: pure functions, comprehensive validation.
 */

import { z } from 'zod';
import { Group, Participant, Expense, CreateGroupParams, AddParticipantParams } from './groupModel';

// Schema validation using Zod
export const ParticipantSchema = z.object({
  id: z.string().min(1, 'Participant ID is required'),
  name: z.string().min(1, 'Name is required').max(50, 'Name too long'),
  email: z.string().email('Invalid email').optional(),
  joinedAt: z.string().datetime('Invalid date format')
});

export const ExpenseSchema = z.object({
  id: z.string().min(1, 'Expense ID is required'),
  description: z.string().min(1, 'Description is required').max(200, 'Description too long'),
  amount: z.number().int().positive('Amount must be positive'),
  currency: z.string().length(3, 'Currency must be 3 characters'),
  paidBy: z.string().min(1, 'Payer is required'),
  splitMode: z.enum(['equal', 'percentage', 'shares', 'custom']),
  participantShares: z.array(z.object({
    participantId: z.string(),
    amount: z.number().optional(),
    percentage: z.number().min(0).max(100).optional(),
    shares: z.number().positive().optional(),
    isIncluded: z.boolean()
  })),
  taxAmount: z.number().int().nonnegative().optional(),
  tipAmount: z.number().int().nonnegative().optional(),
  notes: z.string().max(500, 'Notes too long').optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

export const GroupSchema = z.object({
  id: z.string().min(1, 'Group ID is required'),
  name: z.string().min(1, 'Group name is required').max(100, 'Group name too long'),
  description: z.string().max(500, 'Description too long').optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  schemaVersion: z.string(),
  participants: z.array(ParticipantSchema).min(1, 'At least one participant required'),
  expenses: z.array(ExpenseSchema),
  defaultCurrency: z.string().length(3, 'Currency must be 3 characters'),
  defaultSplitMode: z.enum(['equal', 'percentage', 'shares', 'custom']),
  status: z.enum(['active', 'settled', 'archived'])
});

export const CreateGroupParamsSchema = z.object({
  name: z.string().min(1, 'Group name is required').max(100, 'Group name too long'),
  description: z.string().max(500, 'Description too long').optional(),
  defaultCurrency: z.string().length(3, 'Currency must be 3 characters').optional(),
  creatorName: z.string().min(1, 'Creator name is required').max(50, 'Creator name too long')
});

export const AddParticipantParamsSchema = z.object({
  name: z.string().min(1, 'Name is required').max(50, 'Name too long'),
  email: z.string().email('Invalid email').optional()
});

// Validation functions
export function validateGroup(group: unknown): Group {
  try {
    return GroupSchema.parse(group);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new Error(`Group validation failed: ${error.errors.map(e => e.message).join(', ')}`);
    }
    throw new Error('Group validation failed');
  }
}

export function validateCreateGroupParams(params: unknown): CreateGroupParams {
  try {
    return CreateGroupParamsSchema.parse(params);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new Error(`Invalid group parameters: ${error.errors.map(e => e.message).join(', ')}`);
    }
    throw new Error('Invalid group parameters');
  }
}

export function validateAddParticipantParams(params: unknown): AddParticipantParams {
  try {
    return AddParticipantParamsSchema.parse(params);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new Error(`Invalid participant parameters: ${error.errors.map(e => e.message).join(', ')}`);
    }
    throw new Error('Invalid participant parameters');
  }
}

// Business logic validation
export function validateParticipantName(name: string, existingParticipants: Participant[]): void {
  const trimmedName = name.trim();
  
  if (!trimmedName) {
    throw new Error('Participant name cannot be empty');
  }
  
  if (trimmedName.length > 50) {
    throw new Error('Participant name is too long (max 50 characters)');
  }
  
  const existingNames = existingParticipants.map(p => p.name.toLowerCase());
  if (existingNames.includes(trimmedName.toLowerCase())) {
    throw new Error('A participant with this name already exists');
  }
}

export function validateGroupName(name: string): void {
  const trimmedName = name.trim();
  
  if (!trimmedName) {
    throw new Error('Group name cannot be empty');
  }
  
  if (trimmedName.length > 100) {
    throw new Error('Group name is too long (max 100 characters)');
  }
}

export function validateCurrency(currency: string): void {
  if (!currency || currency.length !== 3) {
    throw new Error('Currency must be a 3-character ISO code (e.g., USD, GEL, RUB)');
  }
  
  // Supported currencies in our app
  const supportedCurrencies = ['USD', 'GEL', 'RUB'];
  if (!supportedCurrencies.includes(currency.toUpperCase())) {
    throw new Error(`Unsupported currency: ${currency}. Supported currencies: ${supportedCurrencies.join(', ')}`);
  }
}

// Type guards
export function isGroup(obj: unknown): obj is Group {
  try {
    GroupSchema.parse(obj);
    return true;
  } catch {
    return false;
  }
}

export function isParticipant(obj: unknown): obj is Participant {
  try {
    ParticipantSchema.parse(obj);
    return true;
  } catch {
    return false;
  }
}

// Sanitization helpers
export function sanitizeGroupName(name: string): string {
  return name.trim().substring(0, 100);
}

export function sanitizeParticipantName(name: string): string {
  return name.trim().substring(0, 50);
}

export function sanitizeDescription(description: string): string {
  return description.trim().substring(0, 500);
}
